import { useQuery } from '@apollo/client';
import { Button, Col, Form, Input, Radio, Row, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import Text from 'antd/lib/typography/Text';
import { getUnixTime, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import DatePicker from 'src/components/ui/date-picker';
import EditorComp from 'src/components/ui/editor';
import { NewsForm } from 'src/lib/form-interfaces';
import {
	ArticleJumpToInput,
	CmsGetArticleCategoriesQuery,
	CmsGetArticleTypesQuery,
	CmsGetAuthorsQuery,
} from 'src/lib/generated/_generated';
import {
	QueryArticleCategories,
	QueryArticleTypes,
	QueryGetAuthors,
} from 'src/lib/gql/queries';
import { Media, MediaFoldersNames } from 'src/lib/interface';
import JumpTo from '../jump-to';
import MediaUpload from '../media-upload';
import SelectTags from '../select-tags';
import SlugInput from '../slug-input';

interface Props {
	newsMedia?: Media | undefined;
	newsName?: string; // Story Name
	newsSlug?: string; //Optional - Link for story
	newsPosition?: number | undefined; //Position of Story
	newsDate?: string; //Selected Dates
	scrapDate?: string; //Selected Dates
	newsContent?: string;
	newsType?: string;
	newsCategory?: string;
	newsTags?: string[];
	newsMeta?: string;
	canDelete?: boolean;
	featured?: boolean;
	enabled?: boolean;
	publishTime?: string;
	jumpTo?: ArticleJumpToInput[] | null;
	isTimely?: boolean;
	authorId?: string;
	onDelete?: React.MouseEventHandler<HTMLElement>; // On Delete Click
	onSave: (data: NewsForm) => void;
}

type NewsFormFields = Omit<NewsForm, 'publishTime'> & {
	publishTime?: Date | null;
};

const getPublishDate = (publishTime?: string) =>
	publishTime ? new Date(Number(publishTime) * 1000) : null;

const NewsCard = (props: Props) => {
	const mediaId = useRef<Media | undefined>(props.newsMedia);
	const [selectedDay, setSelectedDay] = useState<Date | null>(
		props.newsDate ? new Date(props.newsDate) : null,
	);
	const [publishDay, setPublishDay] = useState<Date | null>(
		getPublishDate(props.publishTime),
	);

	const [jumpTo, setJumpTo] = useState<ArticleJumpToInput[]>(
		props?.jumpTo || [],
	);

	const { data: qryData } = useQuery<CmsGetArticleTypesQuery>(
		QueryArticleTypes,
		{
			fetchPolicy: 'cache-and-network',
		},
	);

	const { data: qryCategories } = useQuery<CmsGetArticleCategoriesQuery>(
		QueryArticleCategories,
		{
			fetchPolicy: 'cache-and-network',
		},
	);

	const { data: qryAuthors } = useQuery<CmsGetAuthorsQuery>(QueryGetAuthors, {
		fetchPolicy: 'cache-and-network',
		variables: {
			searchBy: null,
			orderBy: null,
		},
	});

	const [selectedHTags, setSelectedHTags] = useState<string[]>([]);

	const [form] = useForm<NewsFormFields>();

	const { Option } = Select;

	const cityGuideTypeId = useMemo(() => {
		return qryData?.getArticleTypes?.find(
			(type) => type?.name?.toLowerCase() === 'city guide',
		)?.id;
	}, [qryData]);

	const selectedArticleTypeId = Form.useWatch('articleTypeId', form);

	useEffect(() => {
		if (!cityGuideTypeId) {
			return;
		}

		if (selectedArticleTypeId === cityGuideTypeId) {
			form.setFieldsValue({ isTimely: false });
			form.setFieldsValue({ featured: false });
			form.setFieldsValue({ weight: '' });
			setSelectedHTags(selectedHTags?.filter((v) => v !== 'PageNews'));
		}
	}, [cityGuideTypeId, form, selectedArticleTypeId, selectedHTags]);

	const isTimelyForcedDisabled =
		cityGuideTypeId !== undefined && selectedArticleTypeId === cityGuideTypeId;

	useEffect(() => {
		const nextPublishDay = getPublishDate(props.publishTime);

		setPublishDay(nextPublishDay);
		form.setFieldsValue({ publishTime: nextPublishDay });
	}, [form, props.publishTime]);

	const handleDateChange = (day: Date | null) => {
		setPublishDay(day);
	};

	const handlePublishDateChange = (day: Date) => {
		setSelectedDay(day);
	};

	function handlePublishDayChange(day: Date) {
		return getUnixTime(day);
	}

	const createNews = async (data: NewsFormFields) => {
		if (mediaId.current === undefined) {
			CustomNotification({
				pageName: 'News/Articles',
				errorName: 'Cover Image',
				notificationType: 'error',
			});
			return;
		}

		// if (selectedDay === null || selectedDay === undefined) {
		// 	CustomNotification({
		// 		pageName: 'News/Articles',
		// 		errorName: 'Date',
		// 		notificationType: 'error',
		// 	});
		// 	return;
		// }

		const hasPublishDay = publishDay !== null;

		props.onSave({
			title: data.title,
			content: data.content,
			date: selectedDay || new Date(),
			description: data.description,
			mediaId: mediaId.current?.id || '',
			meta: data.meta,
			slug: data.slug,
			articleTypeId: data.articleTypeId,
			articleCategoryId: data.articleCategoryId,
			weight: data.weight,
			tags: selectedHTags,
			scrapDate: data.scrapDate,
			featured: data.featured,
			enabled: hasPublishDay
				? false
				: props.canDelete
				? props.enabled || false
				: true,
			publishTime: hasPublishDay ? handlePublishDayChange(publishDay) : null,
			jumpTo,
			isTimely: data.isTimely || false,
			authorId: data.authorId,
		});
	};

	return (
		<HelmetProvider>
			<Form form={form} onFinish={createNews}>
				<Helmet>
					<script async defer src="//www.instagram.com/embed.js"></script>
				</Helmet>
				<Row style={{ width: '100%', marginTop: '1em' }} gutter={16}>
					<Col span={24}>
						<MediaUpload
							folder={MediaFoldersNames.article}
							onMediaUploaded={(media: Media) => {
								mediaId.current = media;
							}}
							media={mediaId.current}
							accept="IMAGE"
							placeholderText={'Drag and drop a news cover photo'}
						/>
					</Col>

					{/* Name / Title */}
					<Col span={24} lg={18}>
						<label className="input-fields-label">Name / Title</label>

						<Form.Item name="title" initialValue={props.newsName}>
							<Input
								type="text"
								placeholder="Name / Title"
								className="input-fields"
							/>
						</Form.Item>
					</Col>

					{/* Position */}
					<Col span={24} lg={6}>
						<label className="input-fields-label">Position</label>

						<Form.Item
							name="weight"
							initialValue={props.newsPosition}
							rules={[
								{
									validator: (rule, value: string, back) => {
										if (value !== undefined) {
											if (value.length > 4) {
												back('Too large');
												return;
											}
											if (value !== '' && parseInt(value) < 1) {
												back('Minimum value is 1');
												return;
											}
										}
										back(undefined);
										return;
									},
								},
							]}
						>
							<Input
								type="number"
								min={1}
								placeholder="Position / Sequence No."
								className="input-fields"
							/>
						</Form.Item>
					</Col>

					<Col span={24}>
						<label className="input-fields-label">SEO Meta</label>

						<Form.Item
							rules={[{ required: true, min: 50, max: 200 }]}
							name="meta"
							initialValue={props.newsMeta}
						>
							<Input
								type="text"
								placeholder="SEO Meta"
								className="input-fields"
							/>
						</Form.Item>
					</Col>

					<Col span={24} lg={12}>
						<label className="input-fields-label">Slug</label>

						<Form.Item
							name="slug"
							rules={[{ required: true }]}
							initialValue={props.newsSlug}
							hasFeedback
						>
							<SlugInput />
						</Form.Item>
					</Col>

					<Col span={24} lg={12}>
						<label className="input-fields-label">Page Tags</label>

						<SelectTags
							initialValues={props.newsTags || []}
							onSelectionChange={(tags: string[]) => {
								setSelectedHTags(tags);
							}}
							onInit={(tags) => setSelectedHTags(tags)}
						/>
						<Button disabled={!selectedHTags.length}>
							<Link to={`/news/?tags=${selectedHTags.join('_')}`}>
								Find in articles
							</Link>
						</Button>
					</Col>

					<JumpTo jumpTo={jumpTo} setJumpTo={setJumpTo} />

					{/* Content Editor */}
					<Col span={24}>
						<Row className="input-fields">
							<label className="input-fields-label">
								Article / News Content
							</label>

							<Form.Item
								name="content"
								initialValue={props.newsContent}
								style={{
									width: '100%',
								}}
								rules={[{ required: true }]}
							>
								<EditorComp
									value={props.newsContent || '<p>News Content</p>'}
								/>
							</Form.Item>
						</Row>
					</Col>

					<Col span={24} lg={16}>
						<label className="input-fields-label">Article Category</label>

						<Form.Item
							name="articleTypeId"
							rules={[{ required: true }]}
							initialValue={props.newsType}
							hasFeedback
						>
							<Select
								showSearch
								suffixIcon={null}
								style={{ width: '100%' }}
								placeholder="Article Category"
								size="middle"
								className="bg-select"
								optionFilterProp="children"
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{qryData?.getArticleTypes?.map((articleType) => {
									return (
										<Option key={articleType?.id} value={articleType?.id || ''}>
											{articleType?.name}
										</Option>
									);
								})}
							</Select>
						</Form.Item>

						<label className="input-fields-label">Article Type</label>

						<Form.Item
							name="articleCategoryId"
							rules={[{ required: true }]}
							initialValue={props.newsCategory}
							hasFeedback
						>
							<Select
								showSearch
								suffixIcon={null}
								style={{ width: '100%' }}
								placeholder="Article Type"
								size="middle"
								className="bg-select"
								optionFilterProp="children"
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{qryCategories?.getAllArticleCategories?.map(
									(articleCategory) => {
										return (
											<Option
												key={articleCategory?.id}
												value={articleCategory?.id || ''}
											>
												{articleCategory?.name}
											</Option>
										);
									},
								)}
							</Select>
						</Form.Item>

						<label className="input-fields-label">Author</label>

						<Form.Item name="authorId" initialValue={props.authorId}>
							<Select
								showSearch
								suffixIcon={null}
								style={{ width: '100%' }}
								placeholder="Select Author"
								size="middle"
								className="bg-select"
								optionFilterProp="children"
								allowClear
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{qryAuthors?.getAuthors?.map((author) => {
									const fullName = `${author?.fName || ''} ${
										author?.lName || ''
									}`.trim();
									return (
										<Option key={author?.id} value={author?.id || ''}>
											{fullName || 'Unknown Author'}
										</Option>
									);
								})}
							</Select>
						</Form.Item>

						<div>
							<Text
								style={{
									display: 'block',
								}}
							>
								Featured
							</Text>
							<Text
								style={{
									display: 'block',
									fontSize: '0.8em',
									opacity: '0.50',
									marginBottom: '1em',
								}}
							>
								Set article to be featured and shown on every webpage that has
								featured articles.
							</Text>
						</div>

						<Form.Item
							name="featured"
							initialValue={props.featured || false}
							rules={[{ required: true }]}
						>
							<Radio.Group disabled={isTimelyForcedDisabled}>
								<Radio value={true}>Yes</Radio>
								<Radio value={false}>No</Radio>
							</Radio.Group>
						</Form.Item>

						<div>
							<Text
								style={{
									display: 'block',
								}}
							>
								Timely News
							</Text>
							<Text
								style={{
									display: 'block',
									fontSize: '0.8em',
									opacity: '0.50',
									marginBottom: '1em',
								}}
							>
								Mark as timely news for Google News sitemap (automatically
								removed after 48 hours).
							</Text>
						</div>

						<Form.Item
							name="isTimely"
							initialValue={props.isTimely || false}
							rules={[{ required: true }]}
						>
							<Radio.Group disabled={isTimelyForcedDisabled}>
								<Radio value={true}>Yes</Radio>
								<Radio value={false}>No</Radio>
							</Radio.Group>
						</Form.Item>

						<div>
							<Text
								style={{
									display: 'block',
								}}
							>
								Scrap Date
							</Text>
							<Text
								style={{
									display: 'block',
									fontSize: '0.8em',
									opacity: '0.50',
									marginBottom: '1em',
								}}
							>
								Remove the article from showing in the main pages but keep it in
								the archive after the inserted scrap date
							</Text>
						</div>

						<Form.Item
							name="scrapDate"
							initialValue={
								props.scrapDate ? parseISO(props.scrapDate) : undefined
							}
							style={{
								width: '100%',
							}}
						>
							<DatePicker
								placeholder="Scrap Date"
								style={{
									width: 'clamp(10rem, 60% ,100%)',
								}}
							/>
						</Form.Item>
						<div>
							<Text
								style={{
									display: 'block',
								}}
							>
								Scheduled Publish
							</Text>
							<Text
								style={{
									display: 'block',
									fontSize: '0.8em',
									opacity: '0.50',
									marginBottom: '1em',
								}}
							>
								Enter a date and hour to schedule the article to be published.
								Leave empty to publish immediately
							</Text>
						</div>

						<Form.Item
							name="publishTime"
							initialValue={publishDay}
							style={{
								width: '100%',
							}}
						>
							<DatePicker
								onChange={handleDateChange}
								className="margin-0"
								showTime={{ format: 'HH' }}
								format="YYYY-MM-DD HH:mm"
								placeholder="Publish Date and Hour"
								style={{
									width: 'clamp(10rem, 60% ,100%)',
								}}
								inputReadOnly={false}
								onKeyDown={(e) => {
									const input = e.target as HTMLInputElement;
									const allowedKeys = [
										'Backspace',
										'Delete',
										'Tab',
										'Escape',
										'Enter',
										'ArrowLeft',
										'ArrowRight',
										'ArrowUp',
										'ArrowDown',
										'Home',
										'End',
									];

									if (allowedKeys.includes(e.key)) {
										return;
									}

									if (input.value.length >= 16) {
										e.preventDefault();
										return;
									}

									if (!/^[0-9\-:\s]$/.test(e.key)) {
										e.preventDefault();
									}
								}}
							/>
						</Form.Item>
					</Col>

					<Col span={24} lg={8}>
						<Text style={{ display: 'block', marginTop: '1em' }} strong={true}>
							Published Date
						</Text>
						<Text
							style={{ display: 'block', fontSize: '12px', opacity: '0.50' }}
						>
							Click on a date to provide the publish date of the article.
						</Text>

						<DayPicker
							onDayClick={handlePublishDateChange}
							selectedDays={selectedDay || new Date()}
							showOutsideDays={true}
							className="margin-0"
							initialMonth={selectedDay || new Date()}
							disabledDays={(day) => day > new Date()}
						/>
					</Col>

					<Col span={24}>
						<hr style={{ opacity: '0.25' }} />

						<Row justify={'center'}>
							<Space size="small">
								<Button className="btn-main save" htmlType={'submit'}>
									Save
								</Button>

								{props.canDelete ? (
									<Button className="btn-main delete" onClick={props.onDelete}>
										Delete
									</Button>
								) : (
									<Button
										className="btn-main"
										onClick={() => {
											window.history.back();
										}}
									>
										Cancel
									</Button>
								)}
							</Space>
						</Row>
					</Col>
				</Row>
			</Form>
		</HelmetProvider>
	);
};

export default NewsCard;
